import { LineSeries, ResponsiveLine } from '@nivo/line';
import { memo, useMemo } from 'react';
import { Modal } from '../../components/Modal';
import { T } from '../../translation/src';
import { InputSuperadminOrganisationFilters, useSuperadminOrganisationsPerMonthQuery } from '../../types/graphqlTypes';
import { useDarkModePreference } from '../Properties/integration/UserSettingsIntegration';

// At most this many month labels under the chart; the others are left out so they do not overlap.
const MAX_TICKS = 12;

interface MonthPoint {
  x: string;
  y: number;
}

export interface OrganisationsPerMonthModalProps {
  open?: boolean;
  onClose: () => void;
  // The admin center's filters.
  filters: InputSuperadminOrganisationFilters;
}

const monthKey = (date: Date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;

// Turns the server's counts into chart points, one per month.
// Input: sorted by month, months with no new organisations left out.
// Output: every month from the first one up to the current month, missing months as 0.
const fillMonths = (months: { month: string; count: number }[]): MonthPoint[] => {
  if (!months.length) return [];
  const counts = new Map(months.map(({ month, count }) => [month, count]));
  const [year, month] = months[0].month.split('-').map(Number);
  const points: MonthPoint[] = [];
  const cursor = new Date(Date.UTC(year, month - 1, 1));
  const last = monthKey(new Date());
  for (;;) {
    const key = monthKey(cursor);
    points.push({ x: key, y: counts.get(key) ?? 0 });
    if (key >= last) break;
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return points;
};

const OrganisationsPerMonthModal_: React.FC<OrganisationsPerMonthModalProps> = ({ open, onClose, filters }) => {
  const { darkMode } = useDarkModePreference();
  const textColor = darkMode ? '#FFFFFF' : '#000000';
  const lineColor = darkMode ? '#B18CE5' : '#702ECF';
  const { data, loading } = useSuperadminOrganisationsPerMonthQuery({ variables: { filters }, skip: !open, fetchPolicy: 'cache-and-network' });
  const points = useMemo(() => fillMonths(data?.superadminOrganisationsPerMonth ?? []), [data]);
  const series = useMemo<LineSeries[]>(() => [{ id: 'organisations', data: points }], [points]);
  const tickValues = useMemo(() => {
    const step = Math.ceil(points.length / MAX_TICKS);
    return points.filter((_, index) => index % step === 0).map((point) => point.x);
  }, [points]);

  return (
    <Modal.Container open={open} onClose={onClose} panelClassName="w-full max-w-4xl">
      <Modal.Header onClose={onClose}>
        <T _str="new organisations per month" swc />
      </Modal.Header>
      <Modal.Body className="h-96">
        {points.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm text-gray-500">{loading ? <T _str="loading..." /> : <T _str="no organisations" swc />}</div>
        ) : (
          <ResponsiveLine
            data={series}
            margin={{ top: 20, right: 30, bottom: 50, left: 50 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 0, max: 'auto' }}
            axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: -45, tickValues }}
            axisLeft={{ tickSize: 5, tickPadding: 5, format: (value) => (Number.isInteger(Number(value)) ? String(value) : '') }}
            colors={[lineColor]}
            lineWidth={2}
            pointSize={6}
            enableArea
            areaOpacity={0.1}
            enableGridX={false}
            useMesh
            theme={{ axis: { ticks: { text: { fill: textColor } } }, grid: { line: { stroke: darkMode ? '#444444' : '#E5E5E5' } }, crosshair: { line: { stroke: textColor } } }}
            tooltip={({ point }) => (
              <div className="px-2 py-1 text-xs bg-white rounded shadow-lg dark:bg-gray-800 dark:text-white">
                {String(point.data.x)}: <span className="font-bold">{String(point.data.y)}</span>
              </div>
            )}
          />
        )}
      </Modal.Body>
    </Modal.Container>
  );
};

export const OrganisationsPerMonthModal = memo(OrganisationsPerMonthModal_);
